import { useEffect, useMemo, useRef, useState } from "react";
import { FieldArray, Form, Formik } from "formik";
import * as yup from "yup";
import { shallow } from "zustand/shallow";

// === UI Components ===
import { InputTextArea } from "../../../../common/UI/Inputs";
import AppGetIcon from "../../../../common/UI/AppGetIcon";
import DataTable from "../../../../common/UI/DataTable/dataTable";
import Modal from "../../../../common/UI/Modal";
import FileInput from "../../../../common/UI/Inputs/FileInput";
import AppButton from "../../../../common/UI/Buttons/AppButton";

// === Stores ===
import { useHeaderStore } from "../../../../store/header.store";

// === Toast ===
import { toast } from "react-toastify";

// === Estilos ===
import style from "./ComunicationsChannels.module.css";
import styleForm from "../../../../common/theme/styles/form.module.css";
import styleDataTable from "../../../../common/UI/DataTable/dataTable.module.css";

// === Services ===
import { GetComunicationsChannelsWithPaginationService } from "../../services/comunicationsChannels/getComunicationsChannelsWithPagination.service";
import { DeleteWidgetsService } from "../../services/widgets/deleteWidgets.service";
import { GetReactionsService } from "../../services/widgets/getReactions.service";
import { CreateOrUpdateUploadReactionsService } from "../../services/widgets/createOrUpdateUploadReactions.service";
import { DeleteUploadReactionService } from "../../services/widgets/deleteUploadReaction.service";
import { UpdateFromArrayReactionsService } from "../../services/comunicationsChannels/updateFromArrayReactions.service";

// ===================== Tipos =====================
export interface EmotionItem {
  id: number;
  name: string;
  defaultImageUrl?: string;
  defaultMessage?: string;
  message: string;
  imageUrl?: string;
  reactionId?: number;
  reaction?: any;
}

export interface WidgetData {
  channelId: number | null;
  channelName: string;
  type: string;
  apiUrl: string;
  userCount?: number;
  reactions: EmotionItem[];
  createdAt?: string | null;
  updatedAt?: string | null;
}

// ===================== Yup Schema =====================
const ComunicationSchema = yup.object().shape({
  id: yup.number().nullable(),

});

// ===================== Services (instancias) =====================
const deleteWidgetsService = new DeleteWidgetsService();
const createOrUpdateUploadReactionsService = new CreateOrUpdateUploadReactionsService();
const deleteUploadReactionService = new DeleteUploadReactionService();

// ===================== Componente =====================
export default function ComunicationsChannels() {
  const headerStore = useHeaderStore(
    (state) => ({ setModule: state.setModule, setModuleItem: state.setModuleItem }),
    shallow
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isReloadData, setIsReloadData] = useState(false);
  const [isOpenCurrentDelete, setIsOpenCurrentDelete] = useState<any>(null);
  const [showConfirmExitModal, setShowConfirmExitModal] = useState(false);
  const [pendingExitValues, setPendingExitValues] = useState<WidgetData | null>(null);
  const [reopenFormOnCancelExit, setReopenFormOnCancelExit] = useState(false);
  const [originalEditData, setOriginalEditData] = useState<WidgetData | null>(null);
  const [loadingEmotions, setLoadingEmotions] = useState<Record<number, boolean>>({});
  const formikValuesRef = useRef<WidgetData | null>(null);

  const initialForm: WidgetData = useMemo(
    () => ({
      channelId: null,
      channelName: "",
      type: "",
      apiUrl: "",
      reactions: [],
    }),
    []
  );

  const [formData, setFormData] = useState<WidgetData>(initialForm);

  const GetComunicationChannelsPaginatedService = {
    run: async ({ page = 1, perPage = 10, search = "" }) => {
      const response = await GetComunicationsChannelsWithPaginationService();
      const allData = response ?? [];

      const s = search.toLowerCase();
      const filtered = allData.filter((item: any) => {
        const byName = item.channelName?.toLowerCase()?.includes(s);
        const byId = String(item.channelId ?? "").toLowerCase().includes(s);
        return byName || byId;
      });

      const total = filtered.length;
      const startIndex = (page - 1) * perPage;
      const paginatedData = filtered.slice(startIndex, startIndex + perPage);

      return {
        data: paginatedData,
        page,
        perPage,
        total,
      };
    },
  };

  // Header info
  useEffect(() => {
    headerStore.setModule("Administración", "");
    headerStore.setModuleItem("Administración de canales", "/admin/comunicationsChannels");
  }, [headerStore]);

  const handleReaction = async () => {
    const response = await GetReactionsService();
    const normalizedEmotions = mapReactionsToEmotions(response);
    setFormData({ ...initialForm, reactions: normalizedEmotions });
    setIsEditMode(false);
    setIsOpen(true);
  };

  const handleCloseModal = (values?: WidgetData) => {
    if (values && hasUploadedImages(values)) {
      setPendingExitValues(values);
      setShowConfirmExitModal(true);
    } else {
      closeModalCompletely();
    }
  };

  const closeModalCompletely = () => {
    setIsOpen(false);
    setFormData(initialForm);
    setIsEditMode(false);
    setOriginalEditData(null);
  };

  const handleExitAndDeleteUploaded = async () => {
    if (!pendingExitValues) return;

    const updatedReactions = [...pendingExitValues.reactions];

    for (const emo of updatedReactions) {
      if (emo.imageUrl && emo.imageUrl !== emo.defaultImageUrl) {
        try {
          const response: any = await deleteUploadReactionService.run(emo.imageUrl);
          emo.imageUrl = undefined;

          toast.success(
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <AppGetIcon name="checkCircleFill" />
              {response?.message ?? "Archivo eliminado correctamente"}
            </div>,
            { icon: false }
          );
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message || error?.message || "Ocurrió un error al eliminar el archivo";

          toast.error(
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <AppGetIcon name="xCircleFill" />
              {errorMessage}
            </div>,
            { icon: false }
          );
        }
      }
    }

    setFormData(initialForm);
    setIsEditMode(false);
    setPendingExitValues(null);
    setShowConfirmExitModal(false);
    setReopenFormOnCancelExit(false);
  };

  const handleSubmit = async (values: WidgetData) => {
    setLoading(true);

    const payload = {
      reactions: values.reactions.map((emo) => ({
        id: emo.id,
        defaultImageUrl: emo.imageUrl || emo.defaultImageUrl,
        message: emo.message || "",
      })),
    };

    try {
      const result = await UpdateFromArrayReactionsService(payload);

      toast.success(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="checkCircleFill" /> {result.message ?? "Operación realizada correctamente"}
        </div>,
        { icon: false }
      );

      setIsReloadData((prev) => !prev);
      setIsOpen(false);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Error";
      toast.error(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="xCircleFill" />
          {msg}
        </div>,
        { icon: false }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelExit = () => {
    setShowConfirmExitModal(false);

    if (reopenFormOnCancelExit && pendingExitValues) {
      setFormData(pendingExitValues);
      setIsEditMode(!!pendingExitValues.channelId);
      setIsOpen(true);
    }
  };


  const hasImageChangesComparedToInitialData = (current: WidgetData): boolean => {
    if (!originalEditData || !originalEditData.reactions?.length) return false;

    return current.reactions.some((currentReaction) => {
      const initialReaction = originalEditData.reactions.find((r) => r.id === currentReaction.id);
      return !!(initialReaction && currentReaction.imageUrl !== initialReaction.imageUrl);
    });
  };

  // ===================== Columnas DataTable =====================
  const columns = [
    { header: "NOMBRE", field: "channelName" },
    { header: "TIPO", field: "type" },
    { header: "URL API", field: "apiUrl" },
    { header: "TOTAL USUARIOS", field: "userCount" },
  ];

  const hasUploadedImages = (values: WidgetData) =>
    values.reactions.some((emo) => emo.imageUrl && emo.imageUrl !== emo.defaultImageUrl);

  const handleIntentToCloseModal = (values: WidgetData) => {
    setIsOpen(false);

    const isEditingAndChanged = hasImageChangesComparedToInitialData(values);
    const hasNewUploads = hasUploadedImages(values);

    if (isEditingAndChanged || hasNewUploads) {
      setPendingExitValues(values);
      setReopenFormOnCancelExit(true);
      setTimeout(() => setShowConfirmExitModal(true), 300);
    } else {
      closeModalCompletely();
    }
  };


  const mapReactionsToEmotions = (reactions: any[]): EmotionItem[] =>
    reactions.map((reaction) => ({
      id: reaction.id,
      name: reaction.name,
      defaultImageUrl: reaction.defaultImageUrl,
      message: reaction.message,
      imageUrl: reaction.imageUrl,
      reaction: reaction.reaction,
    }));

  // Eliminar logo
  const handleFileDelete = async (
    emotionId: number,
    setFieldValue: (field: string, value: any) => void,
    values: WidgetData
  ) => {
    setLoadingEmotions((prev) => ({ ...prev, [emotionId]: true }));

    try {
      const emotion = values.reactions.find((e) => e.id === emotionId);
      if (!emotion?.imageUrl) return;

      const response: any = await deleteUploadReactionService.run(emotion.imageUrl);

      toast.success(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="checkCircleFill" />
          {response?.message ?? "Archivo eliminado correctamente"}
        </div>,
        { icon: false }
      );

      const updatedEmotions = values.reactions.map((emo) =>
        emo.id === emotionId ? { ...emo, imageUrl: undefined } : emo
      );

      setFieldValue("reactions", JSON.parse(JSON.stringify(updatedEmotions)));

      if (isEditMode) {
        setOriginalEditData((prev) => {
          if (!prev) return prev;
          const newReactions = prev.reactions.map((emo) =>
            emo.id === emotionId ? { ...emo, imageUrl: undefined } : emo
          );
          return { ...prev, reactions: newReactions };
        });
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || "Ocurrió un error al eliminar el archivo";

      toast.error(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="xCircleFill" />
          {errorMessage}
        </div>,
        { icon: false }
      );
    } finally {
      setLoadingEmotions((prev) => ({ ...prev, [emotionId]: false }));
    }
  };

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    emotionId: number,
    setFieldValue: (field: string, value: any) => void,
    values: WidgetData
  ) => {
    setLoadingEmotions((prev) => ({ ...prev, [emotionId]: true }));

    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const dataParams = { file };

      try {
        const response = await createOrUpdateUploadReactionsService.run(dataParams);
        const uploadedUrl = response?.data?.url;

        const updatedEmotions = values.reactions.map((emo) =>
          emo.id === emotionId ? { ...emo, imageUrl: uploadedUrl } : emo
        );

        setFieldValue("reactions", updatedEmotions);

        toast.success(
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <AppGetIcon name="checkCircleFill" />
            {response.message}
          </div>,
          { icon: false }
        );
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        toast.error(
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <AppGetIcon name="xCircleFill" />
            {errorMessage}
          </div>,
          { icon: false }
        );
      } finally {
        setLoadingEmotions((prev) => ({ ...prev, [emotionId]: false }));
      }
    }
  };

  return (
    <div className={style.container}>
      {/* LISTA */}
      <DataTable
        title="Lista de canales"
        columns={columns}
        service={GetComunicationChannelsPaginatedService}
        reloadData={isReloadData}
        excelButton={{
          enabled: true,
          nameFile: `Lista de canales`,
          getExcelData: (data: WidgetData[]) => {
            return data.map((row) => ({
              NOMBRE: row.channelName,
              TIPO: row.type,
              URL_API: row.apiUrl,
              TOTAL_USUARIOS: row.userCount,
            }));
          },
        }}
        moreActions={
          <AppButton
            className={styleDataTable.buttom_option}
            variant="primary"
            iconName="edit"
            buttonText="Reacciones generales"
            unsetWidth
            onClick={handleReaction}
          />
        }
      >
        {""}
      </DataTable>

      {/* MODAL DE REACCIONES */}
      <Modal
        title={"Editar Reacciones por defecto"}
        isOpen={isOpen}
        onClose={() => {
          if (formikValuesRef.current) {
            handleIntentToCloseModal(formikValuesRef.current);
          }
        }}
        size={{ width: "90%", maxWidth: "1300px", height: "" }}
        loadingModal={loading}
        footerContent={
          <div className={style.containerButtoms}>
            <button
              className={styleForm.buttomFooter + " " + styleForm.buttom_cancelar}
              onClick={() => {
                if (formikValuesRef.current) {
                  handleIntentToCloseModal(formikValuesRef.current);
                }
              }}
            >
              Cancelar
            </button>
            <button type="submit" className={styleForm.buttomFooter} form="widgetForm" disabled={loading}>
              Guardar
            </button>
          </div>
        }
      >
        <Formik initialValues={formData} validationSchema={ComunicationSchema} enableReinitialize onSubmit={handleSubmit}>
          {({ values, setFieldValue }) => {
            // Mantener referencia de valores para el cierre controlado del modal
            formikValuesRef.current = values;

            return (
              <Form id="widgetForm" className="flex-row">
                <h4 className={style.under__line}>
                  <strong>Reacciones</strong>
                </h4>

                <div style={{ marginTop: 20 }}>
                  <FieldArray name="reactions">
                    {() => (
                      <div className={style.emotions_container}>
                        {values.reactions.map((emo, idx) => {
                          const currentImg = emo.imageUrl ?? emo.defaultImageUrl;

                          return (
                            <div key={emo.id} className={style.emotion_card}>
                              <h5 className={style.emotion_title}>{emo.reaction?.name ?? emo.name}</h5>

                              <FileInput
                                name={`reactions[${idx}].file`}
                                formats={["png", "jpg", "jpeg", "svg", "gif"]}
                                logoUrl={currentImg}
                                loading={Boolean(loadingEmotions[emo.id])}
                                onChange={(e) => handleFileUpload(e, emo.id, setFieldValue, values)}
                                onDeleteLogo={() => handleFileDelete(emo.id, setFieldValue, values)}
                                isDeletable={Boolean(emo.imageUrl && emo.imageUrl !== emo.defaultImageUrl)}
                              />

                              <InputTextArea
                                name={`reactions[${idx}].message`}
                                classNameInput={styleForm.input__inputWide}
                                placeholder="Mensaje"
                                TextLabel="Mensaje"
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </FieldArray>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal>

      {/* MODAL Confirmar salida */}
      <Modal
        title="Confirmar salida"
        isOpen={showConfirmExitModal}
        onClose={handleCancelExit}
        size={{ width: "500px", height: "" }}
        footerContent={
          <div className={style.containerButtoms}>
            <button className={styleForm.buttomFooter + " " + styleForm.buttom_cancelar} onClick={handleCancelExit}>
              Cancelar
            </button>
            <button className={styleForm.buttomFooter} onClick={handleExitAndDeleteUploaded}>
              Sí, salir y eliminar imágenes
            </button>
          </div>
        }
      >
        <div style={{ padding: 30 }}>
          <center>
            Has subido una o más imágenes.
            <br />
            ¿Estás seguro que deseas salir? Se eliminarán esas imágenes si continúas.
          </center>
        </div>
      </Modal>
    </div>
  );
}
